import {
  Box,
  Container,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Chip,
  TextField,
  Zoom,
  SvgIcon,
  InputAdornment,
} from "@mui/material";
import PageHeading from "../PageHeading";
import Spacing from "../Spacing";
import CompanyCard from "../CompanyCard";
import { CompanyData } from "../CompanyCard/CompanyData";
import { useThemeContext } from "../../context/AppProvider";
import { colors } from "../theme/templateColors";
import { useState, useEffect } from "react";
import { Image } from "../../helper/MiniComponents";
import {
  ArrowDownCircleIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/solid";

const Companies = () => {
  const BetcaLogo = "/images/logo/betca_logo_mainpage.png";
  const theme = useThemeContext();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTag, setSelectedTag] = useState("");
  const [filteredCompanies, setFilteredCompanies] = useState(CompanyData);

  // Extract unique tags from all companies
  const allTags = [
    ...new Set(CompanyData.flatMap((company) => company.tags || [])),
  ].sort();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let filtered = CompanyData;

    if (searchTerm) {
      const lowerTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (company) =>
          company.title.toLowerCase().includes(lowerTerm) ||
          (company.tags || []).some((tag) =>
            tag.toLowerCase().includes(lowerTerm)
          )
      );
    }

    if (selectedTag) {
      filtered = filtered.filter(
        (company) => company.tags && company.tags.includes(selectedTag)
      );
    }

    setFilteredCompanies(filtered);
  }, [searchTerm, selectedTag]);

  return (
    <>
      <PageHeading
        title="Companies in Belagavi"
        bgSrc="/images/about_hero_bg.jpeg"
        pageLinkText="Companies"
      />
      <Container
        maxWidth="xxl"
        sx={{ backgroundColor: theme.mode ? colors.gray : colors.white }}
      >
        <Spacing lg="100" md="80" />
        <Container maxWidth="md">
          <TextField
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SvgIcon>
                      <MagnifyingGlassIcon />
                    </SvgIcon>
                  </InputAdornment>
                ),
              },
            }}
            fullWidth
            label="Search Companies"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {/* Tags Accordion */}
          <Accordion>
            <AccordionSummary
              expandIcon={
                <SvgIcon sx={{ fontSize: 30 }}>
                  <ArrowDownCircleIcon />
                </SvgIcon>
              }
              sx={{ width: "100%" }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                sx={{ width: "100%" }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Filter by Tags
                </Typography>
                <Zoom in={selectedTag} sx={{ mr: 1 }}>
                  <Chip
                    label={`Filtered companies : ${filteredCompanies.length}`}
                  />
                </Zoom>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {allTags.map((tag, i) => (
                  <Chip
                    key={i}
                    label={tag}
                    clickable
                    color={tag === selectedTag ? "primary" : "default"}
                    onClick={() =>
                      setSelectedTag(tag === selectedTag ? "" : tag)
                    }
                  />
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        </Container>

        <Spacing lg="40" md="30" />

        {/* Companies Grid */}
        <Grid
          container
          alignItems="center"
          justifyContent="center"
          columnSpacing={2}
        >
          {filteredCompanies.length === 0 ? (
            <Box
              display="flex"
              flexDirection="column"
              gap={1}
              alignItems="center"
              justifyContent="center"
            >
              <Image image={BetcaLogo} alt="betca_logo" width={80} />
              <Typography variant="h5">
                Sorry, no results found for <strong>"{searchTerm}"</strong>.
              </Typography>
            </Box>
          ) : (
            filteredCompanies.map((details, i) => (
              <Grid key={i} item xs={11} md={6} lg={5.5} sx={{ my: 1.5 }}>
                <Box display="flex" alignItems="center" justifyContent="center">
                  <CompanyCard
                    image={details.image}
                    title={details.title}
                    description1={details.description1}
                    description2={details.description2}
                    description3={details.description3}
                    listItem1={details.listItem1}
                    listItem2={details.listItem2}
                    listItem3={details.listItem3}
                    website={details.website}
                    mail={details.mail}
                    mobile={details.mobile}
                    sx={details.sx}
                  />
                </Box>
              </Grid>
            ))
          )}
        </Grid>

        <Spacing lg="80" md="80" />
      </Container>
    </>
  );
};

export default Companies;
